export type CustomerIntakeFieldType =
  | 'text'
  | 'phone'
  | 'email'
  | 'date'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'textarea'
  | 'consent'
  | 'service'
  | 'custom';

/** The three tiers of the service hierarchy a `service` field can expose. */
export type CustomerIntakeServiceLevel = 'serviceType' | 'subtype' | 'package';

/**
 * Snapshot of the service tree (ids + names only, no pricing) embedded into a
 * `service` field when the admin saves the version, so the public form can
 * render the choices without an authenticated service lookup.
 */
export type CustomerIntakeServiceOptionNode = {
  id: string;
  name: string;
  subtypes?: Array<{
    id: string;
    name: string;
    packages?: Array<{ id: string; name: string }>;
  }>;
};

export type CustomerIntakeServiceConfig = {
  /** `full` = cascade Service Type → Subtype → Service; `single` = one level. */
  mode: 'full' | 'single';
  /** Which level the guest picks when `mode === 'single'`. */
  level?: CustomerIntakeServiceLevel;
  /** Allow the guest to pick more than one service. */
  multiple?: boolean;
  /** Snapshot of the service tree taken when the version was saved. */
  options?: CustomerIntakeServiceOptionNode[];
};

/**
 * A guest's resolved service choice. Stored (JSON-encoded) as the field's
 * answer so the future conversion feature can map it back to real records.
 */
export type CustomerIntakeServiceSelection = {
  serviceTypeId?: string;
  serviceTypeName?: string;
  subtypeId?: string;
  subtypeName?: string;
  packageId?: string;
  packageName?: string;
};

/** Human-readable label for a stored service selection (deepest level wins). */
export const getCustomerIntakeServiceSelectionLabel = (
  selection: CustomerIntakeServiceSelection,
): string =>
  [
    selection.serviceTypeName,
    selection.subtypeName,
    selection.packageName,
  ]
    .filter(Boolean)
    .join(' › ');

export const encodeCustomerIntakeServiceSelection = (
  selection: CustomerIntakeServiceSelection,
): string => JSON.stringify(selection);

export const decodeCustomerIntakeServiceSelection = (
  value: string,
): CustomerIntakeServiceSelection | null => {
  try {
    const parsed = JSON.parse(value) as CustomerIntakeServiceSelection;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export const CUSTOMER_INTAKE_DEFAULT_FIELD_DESCRIPTION =
  'Optional helper text for the guest.';

export const getCustomerIntakeFieldDescription = (description?: string) =>
  description === CUSTOMER_INTAKE_DEFAULT_FIELD_DESCRIPTION
    ? undefined
    : description;

export type CustomerIntakeFieldOption = {
  value: string;
  label: string;
};

export type CustomerIntakeField = {
  id: string;
  systemKey?: string;
  label: string;
  description?: string;
  type: CustomerIntakeFieldType;
  required?: boolean;
  placeholder?: string;
  options?: CustomerIntakeFieldOption[];
  service?: CustomerIntakeServiceConfig;
  conditional?: {
    fieldId: string;
    value: string;
  };
};

export type CustomerIntakeServiceBinding = {
  serviceTypeId: number;
  subtypeId: number;
  packageId: number;
  packageName: string;
};

export type CustomerIntakeSection = {
  id: string;
  title: string;
  description?: string;
  fields: CustomerIntakeField[];
};

export type CustomerIntakeFormConfig = {
  title: string;
  description: string;
  estimatedMinutes: number;
  serviceBindings?: CustomerIntakeServiceBinding[];
  sections: CustomerIntakeSection[];
};

export type CustomerIntakeDefinition = CustomerIntakeFormConfig & {
  schemaVersion: 1;
};

export type CustomerIntakeTemplateStatus = 'DRAFT' | 'ACTIVE';

export type CustomerIntakeTemplateVersion = CustomerIntakeFormConfig & {
  id: string;
  name: string;
  version: string;
  status: CustomerIntakeTemplateStatus;
  isLocked?: boolean;
  responseCount: number;
  updatedAt: string;
};

export type CustomerIntakeResponse = {
  id: string;
  templateId: string;
  submittedAt: string;
  customerName: string;
  phone: string;
  email: string;
  answers: CustomerIntakeFormValues;
};

export type CustomerIntakeFormValue = string | string[] | boolean | number;

export type CustomerIntakeFormValues = Record<string, CustomerIntakeFormValue>;

export type CustomerIntakeFormErrors = Record<string, string>;
